/**
 * Project List Filters
 * Handles the filtering functionality for the project list module
 * Uses Alpine.js for reactive state management
 */

export function projectListFilters({ defaultFilter = 'property-management' } = {}) {
  return {
    activeFilter: defaultFilter,
    
    /**
     * Initialize the component
     */
    init() {
      // Set initial filter from URL hash if present
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        this.activeFilter = hash;
      }
    },

    /**
     * Filter projects by division/property type
     * @param {string} filter - The filter slug to apply
     */
    filterProjects(filter) {
      this.activeFilter = filter;
      
      // Update URL hash without scrolling
      if (filter !== 'property-management') {
        history.replaceState(null, null, `#${filter}`);
      } else {
        history.replaceState(null, null, window.location.pathname);
      }
    }
  };
}

/**
 * Project List with Load More
 * Extends projectListFilters with "Load More" pagination.
 * Used when the project list block has no entry limit configured.
 *
 * @param {object} config
 * @param {number} config.pmTotal  - Total property-management projects (server-side count)
 * @param {number} config.devTotal - Total development projects (server-side count)
 */
export function projectListWithLoadMore({ pmTotal = 0, devTotal = 0, defaultFilter = 'property-management' } = {}) {
  const base = projectListFilters({ defaultFilter });
  const batchSize = 5;

  return {
    ...base,

    pmOffset: batchSize,
    devOffset: batchSize,
    pmHasMore: pmTotal > batchSize,
    devHasMore: devTotal > batchSize,
    loading: false,

    get showLoadMore() {
      if (this.activeFilter === 'property-management') return this.pmHasMore;
      if (this.activeFilter === 'development') return this.devHasMore;
      return false;
    },

    async loadMore() {
      if (this.loading || !this.showLoadMore) return;

      const filter = this.activeFilter;
      const offset = filter === 'property-management' ? this.pmOffset : this.devOffset;
      const refKey = filter === 'property-management' ? 'pmGrid' : 'devGrid';

      this.loading = true;

      try {
        const res = await fetch(
          `/api/projects?filter=${encodeURIComponent(filter)}&offset=${offset}`
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (data.html) {
          this.$refs[refKey].insertAdjacentHTML(
            'beforeend',
            `<div class="mt-5">${data.html}</div>`
          );
        }

        if (filter === 'property-management') {
          this.pmOffset += batchSize;
          this.pmHasMore = data.hasMore;
        } else {
          this.devOffset += batchSize;
          this.devHasMore = data.hasMore;
        }
      } catch (err) {
        console.error('Failed to load more projects:', err);
      } finally {
        this.loading = false;
      }
    },
  };
}

// Make globally available for Alpine.js
window.projectListFilters = projectListFilters;
window.projectListWithLoadMore = projectListWithLoadMore;
